import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { API } from '@/utils/constants';

export const NewsletterPage = () => {
  // Settings state
  const [settings, setSettings] = useState(null);
  const [apiKey, setApiKey] = useState('');
  const [testingConnection, setTestingConnection] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  
  // Newsletter state
  const [customerTypes, setCustomerTypes] = useState([]);
  const [selectedType, setSelectedType] = useState('');
  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [brevoLists, setBrevoLists] = useState([]);

  // Webhook state (for unsubscribe sync)
  const [webhookInfo, setWebhookInfo] = useState(null);
  const [rotatingSecret, setRotatingSecret] = useState(false);
  const [freshSecret, setFreshSecret] = useState('');   // shown once after rotation
  const [webhookEvents, setWebhookEvents] = useState([]);

  useEffect(() => {
    fetchSettings();
    fetchCustomerTypes();
    fetchSyncStatus();
    fetchWebhookInfo();
    fetchWebhookEvents();
  }, []);

  useEffect(() => {
    if (selectedType) {
      fetchContacts(selectedType);
    }
  }, [selectedType]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/newsletter/settings`);
      setSettings(response.data.brevo);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const fetchCustomerTypes = async () => {
    try {
      const response = await axios.get(`${API}/newsletter/customer-types`);
      setCustomerTypes(response.data.customer_types);
    } catch (error) {
      console.error('Error fetching customer types:', error);
    }
  };

  const fetchContacts = async (type) => {
    setLoadingContacts(true);
    try {
      const response = await axios.get(`${API}/newsletter/contacts-by-type/${encodeURIComponent(type)}`);
      setContacts(response.data.contacts);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast.error('Failed to load contacts');
    } finally {
      setLoadingContacts(false);
    }
  };

  const fetchSyncStatus = async () => {
    try {
      const response = await axios.get(`${API}/newsletter/sync-status`);
      setSyncStatus(response.data);
    } catch (error) {
      console.error('Error fetching sync status:', error);
    }
  };

  const fetchBrevoLists = async () => {
    try {
      const response = await axios.get(`${API}/newsletter/brevo-lists`);
      setBrevoLists(response.data.lists);
    } catch (error) {
      console.error('Error fetching Brevo lists:', error);
    }
  };

  const handleSaveApiKey = async () => {
    if (!apiKey.trim()) {
      toast.error('Please enter an API key');
      return;
    }
    
    setSavingSettings(true);
    try {
      await axios.put(`${API}/newsletter/settings`, null, {
        params: { api_key: apiKey, enabled: true }
      });
      toast.success('API key saved successfully');
      setApiKey('');
      fetchSettings();
      fetchBrevoLists();
    } catch (error) {
      console.error('Error saving API key:', error);
      toast.error('Failed to save API key');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleTestConnection = async () => {
    setTestingConnection(true);
    try {
      const response = await axios.post(`${API}/newsletter/test-connection`);
      toast.success(`${response.data.message} Connected as: ${response.data.account_email}`);
      fetchBrevoLists();
    } catch (error) {
      console.error('Error testing connection:', error);
      toast.error(error.response?.data?.detail || 'Connection test failed');
    } finally {
      setTestingConnection(false);
    }
  };

  // ── Webhook (unsubscribe sync) helpers ─────────────────────────────────
  const fetchWebhookInfo = async () => {
    try {
      const response = await axios.get(`${API}/newsletter/webhook-info`);
      setWebhookInfo(response.data);
    } catch (error) {
      // 403 for non-admins is expected — silently ignore
      if (error.response?.status !== 403) console.error('Error fetching webhook info:', error);
    }
  };

  const fetchWebhookEvents = async () => {
    try {
      const response = await axios.get(`${API}/newsletter/webhook-events?limit=25`);
      setWebhookEvents(response.data.events || []);
    } catch (error) {
      if (error.response?.status !== 403) console.error('Error fetching webhook events:', error);
    }
  };

  const handleRotateSecret = async () => {
    const confirmed = webhookInfo?.has_secret
      ? window.confirm('Rotating the secret will INVALIDATE the current one in Brevo. You must paste the new secret into Brevo\'s webhook config afterwards. Continue?')
      : true;
    if (!confirmed) return;
    setRotatingSecret(true);
    try {
      const response = await axios.post(`${API}/newsletter/webhook-secret/rotate`);
      setFreshSecret(response.data.webhook_secret);
      toast.success('New secret generated — copy it into Brevo now.');
      fetchWebhookInfo();
    } catch (error) {
      console.error('Error rotating secret:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate secret');
    } finally {
      setRotatingSecret(false);
    }
  };

  const handleClearSecret = async () => {
    if (!window.confirm('Remove the shared secret? The endpoint will accept any caller — only do this while debugging.')) return;
    try {
      await axios.delete(`${API}/newsletter/webhook-secret`);
      setFreshSecret('');
      toast.success('Secret cleared.');
      fetchWebhookInfo();
    } catch (error) {
      toast.error('Failed to clear secret');
    }
  };

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied to clipboard`);
    } catch {
      toast.error('Copy failed — copy manually');
    }
  };

  const handleSync = async () => {
    if (!selectedType) {
      toast.error('Please select a customer type first');
      return;
    }
    
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/newsletter/sync`, {
        referral_source: selectedType  // API uses this field name for compatibility
      });
      toast.success(response.data.message);
      fetchContacts(selectedType);
      fetchSyncStatus();
      fetchBrevoLists();
    } catch (error) {
      console.error('Error syncing contacts:', error);
      toast.error(error.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Newsletter</h1>
          <p className="text-muted-foreground">Sync your contacts to Brevo for email marketing</p>
        </div>
      </div>

      <Tabs defaultValue="sync" className="w-full">
        <TabsList>
          <TabsTrigger value="sync" data-testid="newsletter-sync-tab">Sync Contacts</TabsTrigger>
          <TabsTrigger value="settings" data-testid="newsletter-settings-tab">Settings</TabsTrigger>
        </TabsList>

        {/* Sync Tab */}
        <TabsContent value="sync" className="space-y-6">
          {/* Connection Status */}
          <Card>
            <CardHeader>
              <CardTitle>Brevo Connection</CardTitle>
              <CardDescription>
                {settings?.has_api_key 
                  ? 'Your Brevo account is connected' 
                  : 'Configure your Brevo API key in the Settings tab to get started'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <Badge variant={settings?.has_api_key ? 'default' : 'secondary'}>
                  {settings?.has_api_key ? '✓ Connected' : '○ Not Connected'}
                </Badge>
                {syncStatus?.last_sync && (
                  <span className="text-sm text-muted-foreground">
                    Last sync: {formatDate(syncStatus.last_sync)}
                  </span>
                )}
                {syncStatus?.total_synced > 0 && (
                  <Badge variant="outline">
                    {syncStatus.total_synced} contacts synced
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Select Group */}
          {settings?.has_api_key && (
            <>
              <Card>
                <CardHeader>
                  <CardTitle>Select Contact Group</CardTitle>
                  <CardDescription>Choose a customer type to view and sync contacts. Set customer type when adding jobs.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex gap-4 items-end">
                    <div className="flex-1">
                      <Label htmlFor="type-select">Customer Type</Label>
                      <Select value={selectedType} onValueChange={setSelectedType}>
                        <SelectTrigger id="type-select" data-testid="customer-type-select">
                          <SelectValue placeholder="Select a group..." />
                        </SelectTrigger>
                        <SelectContent>
                          {customerTypes.map((item) => (
                            <SelectItem key={item.type} value={item.type}>
                              {item.type} ({item.contact_count} contacts)
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button 
                      onClick={handleSync} 
                      disabled={!selectedType || syncing || contacts.length === 0}
                      data-testid="sync-to-brevo-btn"
                    >
                      {syncing ? 'Syncing...' : `Sync to Brevo`}
                    </Button>
                  </div>
                  
                  {selectedType && syncStatus?.sync_by_source?.[selectedType] > 0 && (
                    <div className="text-sm text-green-600">
                      ✓ {syncStatus.sync_by_source[selectedType]} contacts already synced from this group
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Contacts Table */}
              {selectedType && (
                <Card>
                  <CardHeader>
                    <CardTitle>Contacts in "{selectedType}"</CardTitle>
                    <CardDescription>
                      {contacts.length} contacts with email addresses
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {loadingContacts ? (
                      <div className="text-center py-8">Loading contacts...</div>
                    ) : contacts.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        No contacts with email addresses found in this group.
                        <br />
                        <span className="text-sm">Set the "Customer Type" field when adding new jobs to categorize customers.</span>
                      </div>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Name</TableHead>
                            <TableHead>Email</TableHead>
                            <TableHead>Sync Status</TableHead>
                            <TableHead>Synced At</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {contacts.map((contact, idx) => (
                            <TableRow key={contact.email + idx}>
                              <TableCell>{contact.name || '-'}</TableCell>
                              <TableCell>{contact.email}</TableCell>
                              <TableCell>
                                {contact.synced_to_brevo ? (
                                  <Badge variant="default" className="bg-green-100 text-green-800">
                                    ✓ Synced
                                  </Badge>
                                ) : (
                                  <Badge variant="secondary">
                                    Pending
                                  </Badge>
                                )}
                              </TableCell>
                              <TableCell>
                                {contact.synced_at ? formatDate(contact.synced_at) : '-'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Brevo Lists */}
              {brevoLists.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Your Brevo Lists</CardTitle>
                    <CardDescription>Lists created in your Brevo account</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>List Name</TableHead>
                          <TableHead>Subscribers</TableHead>
                          <TableHead>ID</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {brevoLists.map((list) => (
                          <TableRow key={list.id}>
                            <TableCell>{list.name}</TableCell>
                            <TableCell>{list.total_subscribers}</TableCell>
                            <TableCell className="text-muted-foreground">{list.id}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}
            </>
          )}

          {/* Not Connected Message */}
          {!settings?.has_api_key && (
            <Card>
              <CardContent className="py-12">
                <div className="text-center space-y-4">
                  <div className="text-6xl">📧</div>
                  <h3 className="text-xl font-semibold">Connect Your Brevo Account</h3>
                  <p className="text-muted-foreground max-w-md mx-auto">
                    To sync contacts and send newsletters, you need to connect your Brevo account.
                    Go to the Settings tab to add your API key.
                  </p>
                  <Button onClick={() => document.querySelector('[value="settings"]')?.click()}>
                    Go to Settings
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Brevo API Configuration</CardTitle>
              <CardDescription>
                Connect your Brevo account to enable newsletter sync.
                Get your API key from <a href="https://app.brevo.com/settings/keys/api" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Brevo Dashboard → SMTP & API → API Keys</a>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Current Status */}
              {settings?.has_api_key && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Badge variant="default" className="bg-green-600">Connected</Badge>
                    <span className="text-sm">API Key: {settings.api_key_masked}</span>
                  </div>
                  <div className="mt-3">
                    <Button 
                      variant="outline" 
                      onClick={handleTestConnection}
                      disabled={testingConnection}
                      data-testid="test-connection-btn"
                    >
                      {testingConnection ? 'Testing...' : 'Test Connection'}
                    </Button>
                  </div>
                </div>
              )}

              {/* Add/Update API Key */}
              <div className="space-y-4">
                <h4 className="font-medium">{settings?.has_api_key ? 'Update API Key' : 'Add API Key'}</h4>
                <div className="flex gap-4">
                  <div className="flex-1">
                    <Input
                      type="password"
                      placeholder="Enter your Brevo API key (xkeysib-...)"
                      value={apiKey}
                      onChange={(e) => setApiKey(e.target.value)}
                      data-testid="brevo-api-key-input"
                    />
                  </div>
                  <Button 
                    onClick={handleSaveApiKey}
                    disabled={savingSettings || !apiKey.trim()}
                    data-testid="save-api-key-btn"
                  >
                    {savingSettings ? 'Saving...' : 'Save API Key'}
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground">
                  Your API key is stored securely and never displayed in full after saving.
                </p>
              </div>

              {/* Help Section */}
              <div className="border-t pt-6 mt-6">
                <h4 className="font-medium mb-4">How to get your Brevo API Key</h4>
                <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                  <li>Go to <a href="https://www.brevo.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">brevo.com</a> and sign up for a free account</li>
                  <li>Once logged in, click your name in the top right → SMTP & API</li>
                  <li>Click "API Keys" tab</li>
                  <li>Click "Generate a new API key"</li>
                  <li>Copy the key and paste it above</li>
                </ol>
                <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm">
                    <strong>Free Plan:</strong> Brevo's free plan includes 300 emails/day and unlimited contacts - perfect for getting started!
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ── Unsubscribe Webhook Sync ───────────────────────────────── */}
          <Card data-testid="brevo-webhook-card">
            <CardHeader>
              <CardTitle>Unsubscribe Sync (Webhook)</CardTitle>
              <CardDescription>
                When a customer clicks "unsubscribe" in a Brevo email, Brevo POSTs an event
                to this app. We then flag them as unsubscribed so they're excluded from all
                future syncs. Also handles hard bounces and spam complaints.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Endpoint URL */}
              <div className="space-y-2">
                <Label>Webhook Endpoint URL</Label>
                <div className="flex gap-2">
                  <Input
                    readOnly
                    value={webhookInfo?.webhook_url || ''}
                    className="font-mono text-sm"
                    data-testid="webhook-url-input"
                  />
                  <Button
                    variant="outline"
                    onClick={() => copyToClipboard(webhookInfo?.webhook_url || '', 'Webhook URL')}
                    data-testid="copy-webhook-url-btn"
                  >
                    Copy
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Paste this into Brevo → Transactional → Settings → Webhooks (also under Contacts → Settings → Unsubscribe webhooks for marketing lists).
                </p>
              </div>

              {/* Signing secret */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Signing Secret {webhookInfo?.has_secret && <Badge variant="default" className="ml-2 bg-green-600">Configured</Badge>}</Label>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleRotateSecret}
                      disabled={rotatingSecret}
                      data-testid="rotate-webhook-secret-btn"
                    >
                      {rotatingSecret ? 'Generating…' : (webhookInfo?.has_secret ? 'Rotate' : 'Generate')}
                    </Button>
                    {webhookInfo?.has_secret && (
                      <Button variant="ghost" size="sm" onClick={handleClearSecret} data-testid="clear-webhook-secret-btn">
                        Clear
                      </Button>
                    )}
                  </div>
                </div>

                {freshSecret ? (
                  <div className="p-3 bg-amber-50 border border-amber-300 rounded-lg space-y-2">
                    <p className="text-xs font-semibold text-amber-800">
                      ⚠️ Copy this NOW — you won't be able to see it again after leaving this page.
                    </p>
                    <div className="flex gap-2">
                      <Input readOnly value={freshSecret} className="font-mono text-xs bg-white" data-testid="fresh-secret-input" />
                      <Button size="sm" variant="outline" onClick={() => copyToClipboard(freshSecret, 'Secret')} data-testid="copy-fresh-secret-btn">
                        Copy
                      </Button>
                    </div>
                    <p className="text-xs text-amber-800">
                      Paste it into Brevo's webhook config as <code className="bg-white px-1 rounded">Secret</code>, then Save.
                    </p>
                  </div>
                ) : webhookInfo?.has_secret ? (
                  <p className="text-sm text-muted-foreground">
                    Secret ending in <code className="text-xs">{webhookInfo.secret_masked}</code> — hidden for security. Click <em>Rotate</em> to reveal a new one.
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No signing secret set. Click <em>Generate</em> to create one (recommended — otherwise anyone can POST fake events).
                  </p>
                )}
              </div>

              {/* Setup instructions */}
              <div className="border-t pt-6">
                <h4 className="font-medium mb-3">How to configure in Brevo</h4>
                <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                  <li>Click <strong>Generate</strong> above and copy the secret.</li>
                  <li>In Brevo, go to{' '}
                    <a href="https://app.brevo.com/settings/webhook/list" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
                      Transactional → Settings → Webhooks
                    </a>{' '}
                    and click <em>Add a new webhook</em>.
                  </li>
                  <li>Paste the <strong>Webhook URL</strong> above into the URL field.</li>
                  <li>Paste the <strong>Signing Secret</strong> into the <em>Secret</em> field (if Brevo asks for one).</li>
                  <li>Check these events: <strong>Unsubscribed</strong>, <strong>Hard bounce</strong>, <strong>Spam</strong>.</li>
                  <li>Save. Brevo will now notify us whenever a customer unsubscribes.</li>
                  <li>For marketing campaigns, also add a webhook under{' '}
                    <a href="https://app.brevo.com/contact/list-listing" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Contacts → Settings → Unsubscribe webhooks</a>{' '}
                    using the same URL and secret.
                  </li>
                </ol>
              </div>

              {/* Recent Events */}
              <div className="border-t pt-6">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium">Recent Events {webhookEvents.length > 0 && <span className="text-sm text-muted-foreground">({webhookEvents.length} shown)</span>}</h4>
                  <Button variant="ghost" size="sm" onClick={fetchWebhookEvents} data-testid="refresh-webhook-events-btn">
                    Refresh
                  </Button>
                </div>
                {webhookEvents.length === 0 ? (
                  <p className="text-sm text-muted-foreground p-4 bg-muted/30 rounded-lg text-center">
                    No webhook events received yet. Once Brevo is configured, unsubscribes and bounces will appear here.
                  </p>
                ) : (
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>When</TableHead>
                          <TableHead>Event</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead className="text-right">Result</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {webhookEvents.map((e) => (
                          <TableRow key={e.id} data-testid={`webhook-event-row-${e.id}`}>
                            <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                              {formatDate(e.received_at)}
                            </TableCell>
                            <TableCell>
                              <Badge variant={e.resulted_in_optout ? 'destructive' : 'secondary'}>
                                {e.event}
                              </Badge>
                            </TableCell>
                            <TableCell className="font-mono text-xs">{e.email}</TableCell>
                            <TableCell className="text-right text-xs">
                              {e.resulted_in_optout
                                ? <span className="text-red-700 font-medium">Opted out</span>
                                : <span className="text-muted-foreground">Logged</span>}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default NewsletterPage;
